import { NgClass, NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, forwardRef, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ButtonComponent } from '@examdojo/core/button';
import { NodeViewComponent } from '@examdojo/core/tiptap';
import katex from 'katex';
import { v4 as uuid } from 'uuid';
import { DataDisplayBlock } from '../../../model';
import { DragWithHandleOnlyDirective } from '../drag-with-handle-only.directive';
import { DEFAULT_DATA_DISPLAY_PROPS } from './data-display.model';

@Component({
  selector: 'dojo-data-display-node',
  standalone: true,
  imports: [NgClass, NgStyle, FormsModule, ButtonComponent],
  template: `
    <div class="data-display-wrapper" [ngClass]="customCss()" [ngStyle]="customStyle() ?? {}">
      @if (!isEditing()) {
        <div class="data-display-content" [innerHTML]="renderedHtml()"></div>

        @if (editor().isEditable) {
          <button class="edit-btn" (click)="startEdit()" title="Edit data">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </button>
        }
      }

      @if (isEditing()) {
        <div class="data-display-edit">
          <textarea
            class="data-display-textarea"
            [ngModel]="editValue()"
            (ngModelChange)="editValue.set($event)"
            placeholder="Enter comma-separated values, e.g. 23, 35, 42, 44"
            rows="2"
            (keydown.escape)="cancelEdit()"
            (keydown.meta.enter)="applyEdit()"
            (keydown.control.enter)="applyEdit()"
          ></textarea>
          <div class="data-display-edit-actions">
            <label class="sorted-checkbox">
              <input type="checkbox" [ngModel]="editSorted()" (ngModelChange)="editSorted.set($event)" />
              Sorted
            </label>
            <div class="edit-buttons">
              <dojo-button fill="outline" size="s" (click)="cancelEdit()">Cancel</dojo-button>
              <dojo-button size="s" (click)="applyEdit()">Apply</dojo-button>
            </div>
          </div>
        </div>
      }
    </div>
  `,
  styles: `
    :host {
      @apply block my-4;

      &.ProseMirror-selectednode {
        background-color: transparent !important;
        outline: none !important;
      }
    }

    .data-display-wrapper {
      position: relative;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 12px 16px;
      background: #fafafa;

      &:hover .edit-btn {
        opacity: 1;
      }
    }

    .data-display-content {
      font-size: 0.875rem;
      line-height: 2;
      text-align: center;

      ::ng-deep .data-value {
        display: inline;
        white-space: nowrap;
      }
    }

    .data-display-edit {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .data-display-textarea {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      font-family: monospace;
      font-size: 0.875rem;
      resize: vertical;
      outline: none;

      &:focus {
        border-color: #3b82f6;
        box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
      }
    }

    .data-display-edit-actions {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .sorted-checkbox {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 0.875rem;
      color: #6b7280;
      cursor: pointer;
    }

    .edit-buttons {
      display: flex;
      gap: 6px;
    }

    .edit-btn {
      position: absolute;
      top: 4px;
      right: 4px;
      padding: 6px;
      background: white;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      cursor: pointer;
      opacity: 0;
      transition: opacity 0.15s, background-color 0.15s;
      color: #6b7280;
      z-index: 10;

      &:hover {
        background: #f3f4f6;
        color: #374151;
      }
    }
  `,
  hostDirectives: [DragWithHandleOnlyDirective],
  providers: [
    {
      provide: NodeViewComponent,
      useExisting: forwardRef(() => DataDisplayNodeComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataDisplayNodeComponent extends NodeViewComponent<DataDisplayBlock> {
  private readonly sanitizer = inject(DomSanitizer);

  readonly data = this.getProp('data');
  readonly sorted = this.getProp('sorted');
  readonly customCss = this.getProp('custom_css');
  readonly customStyle = this.getProp('custom_style');

  readonly isEditing = signal(false);
  readonly editValue = signal('');
  readonly editSorted = signal(false);

  readonly renderedHtml = computed<SafeHtml>(() => {
    const raw = this.data();
    if (!raw || !raw.trim()) {
      return this.sanitizer.bypassSecurityTrustHtml(
        '<span style="color: #9ca3af; font-style: italic;">No data entered</span>',
      );
    }

    const values = raw
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v.length > 0);

    try {
      const parts = values.map((v, i) => {
        const rendered = katex.renderToString(v, { throwOnError: false, displayMode: false, output: 'html' });
        const separator = i < values.length - 1 ? ',\u2002' : '';
        return `<span class="data-value">${rendered}${separator}</span>`;
      });
      return this.sanitizer.bypassSecurityTrustHtml(parts.join(''));
    } catch {
      return this.sanitizer.bypassSecurityTrustHtml(`<span style="color: #ef4444;">Invalid data: ${raw}</span>`);
    }
  });

  override getDefaults = (): DataDisplayBlock => ({
    ...DEFAULT_DATA_DISPLAY_PROPS,
    id: uuid(),
  });

  startEdit(): void {
    this.editValue.set(this.data() ?? '');
    this.editSorted.set(this.sorted() ?? false);
    this.isEditing.set(true);
  }

  applyEdit(): void {
    this.updateProps({
      data: this.editValue(),
      sorted: this.editSorted(),
    });
    this.isEditing.set(false);
  }

  cancelEdit(): void {
    this.isEditing.set(false);
  }
}
